import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import logger from '../../utils/logger';
import { useAuth } from '../../contexts/AuthContext';
import { useModal } from '../../contexts/ModalContext';
import { usePermissions } from '../../hooks/usePermissions';
import { db } from '../../firebase';
import { doc, updateDoc, deleteField } from 'firebase/firestore';
import Layout from '../../components/Layout';
import Card from '../../ui/Card';
import Button from '../../ui/Button';
import ModuleCard from '../../ui/ModuleCard';
import EmptyState from '../../ui/EmptyState';
import { ICONS } from '../../ui/icon-map';
import { ALL_NAV_ITEMS } from '../../ui/AppShell/navigation';
import '../../ui/ds.css';

// Modules: every page the user may open, as a card, in the user's own order (owner, 1 Oct 2026: a page of its own in
// the sidebar - the Overview is the home page). The cards come from the shared navigation, the sidebar's own list.
// The order is saved per user in the database (users/<uid>.dashboardOrder, by page path - a path never changes when a
// page is renamed), so it follows the user to any device; the browser keeps a copy for the first paint.
const NOT_A_CARD = new Set(['dashboard', 'modules']);

const Modules = () => {
    const { currentUser } = useAuth();
    const { hasPermission } = usePermissions();
    const { showToast } = useModal();

    useEffect(() => { document.title = 'Modules · Yelo Dubai'; }, []);

    const allProjects = useMemo(() => ALL_NAV_ITEMS
        .filter((i) => !NOT_A_CARD.has(i.id))
        .map((i) => ({ title: i.label, icon: i.icon, path: i.path, desc: i.description, moduleId: i.permission, group: i.group })), []);

    const [projectOrder, setProjectOrder] = useState([]);
    const orderKey = `dashboard_order_v2:${currentUser?.uid || ''}`;
    useEffect(() => {
        if (Array.isArray(currentUser?.dashboardOrder)) { setProjectOrder(currentUser.dashboardOrder); return; }
        try {
            const kept = localStorage.getItem(orderKey);
            if (kept) setProjectOrder(JSON.parse(kept));
        } catch (e) {
            logger.error('Failed to parse saved module order', e);
        }
    }, [currentUser?.dashboardOrder, orderKey]);
    const saveOrder = useCallback(async (paths) => {
        try { if (paths) localStorage.setItem(orderKey, JSON.stringify(paths)); else localStorage.removeItem(orderKey); } catch (e) { /* storage blocked */ }
        if (!currentUser?.uid) return;
        try {
            await updateDoc(doc(db, 'users', currentUser.uid), { dashboardOrder: paths || deleteField() });
        } catch (e) {
            logger.warn('Could not save the module order', e);
            showToast('The order is kept on this browser only (could not save it to your account)', 'warning');
        }
    }, [currentUser?.uid, orderKey, showToast]);

    // the sidebar's own rule: a module without a permission is open to everyone, otherwise the user needs it
    const visibleProjects = useMemo(() => {
        const accessible = allProjects.filter((p) => !p.moduleId || hasPermission(p.moduleId));
        if (projectOrder.length === 0) return accessible;     // no custom order: the sidebar's order
        return [...accessible].sort((a, b) => {
            const idxA = projectOrder.indexOf(a.path);
            const idxB = projectOrder.indexOf(b.path);
            if (idxA !== -1 && idxB !== -1) return idxA - idxB;
            if (idxA !== -1) return -1;
            if (idxB !== -1) return 1;
            return 0;
        });
    }, [allProjects, projectOrder, hasPermission]);

    // drag and drop reordering (mouse)
    const [draggedItem, setDraggedItem] = useState(null);
    const handleDragStart = (e, index) => {
        setDraggedItem(visibleProjects[index]);
        e.dataTransfer.effectAllowed = 'move';
    };
    const handleDragOver = (e) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
    };
    const handleDrop = (e, targetIndex) => {
        e.preventDefault();
        if (!draggedItem) return;
        const sourceIndex = visibleProjects.findIndex((p) => p.title === draggedItem.title);
        if (sourceIndex === targetIndex) {
            setDraggedItem(null);
            return;
        }
        const next = [...visibleProjects];
        const [moved] = next.splice(sourceIndex, 1);
        next.splice(targetIndex, 0, moved);
        const order = next.map((p) => p.path);
        setProjectOrder(order);
        saveOrder(order);
        setDraggedItem(null);
    };
    const resetOrder = () => {
        setProjectOrder([]);
        saveOrder(null);
        try { localStorage.removeItem('dashboard_order'); } catch (e) { /* storage blocked */ }
        showToast('Module order reset to the sidebar order', 'success');
    };

    return (
        <Layout
            title="Modules"
            subtitle="Every page you can open · drag a card to reorder"
            actions={projectOrder.length > 0 ? <Button size="sm" variant="ghost" onClick={resetOrder}>Reset order</Button> : null}
        >
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-5)' }}>
                {visibleProjects.length === 0 ? (
                    <Card>
                        <EmptyState icon={ICONS.lock} title="No modules assigned to your account" description="Ask an administrator for access." />
                    </Card>
                ) : (
                    <div className="dash-modules">
                        {visibleProjects.map((project, idx) => (
                            <div
                                key={project.title}
                                draggable
                                onDragStart={(e) => handleDragStart(e, idx)}
                                onDragOver={handleDragOver}
                                onDrop={(e) => handleDrop(e, idx)}
                                onDragEnd={() => setDraggedItem(null)}
                                className={`dash-modules__cell ${draggedItem && draggedItem.title === project.title ? 'is-dragging' : ''}`}
                            >
                                <ModuleCard title={project.title} desc={project.desc} path={project.path} iconGlyph={project.icon} />
                            </div>
                        ))}
                    </div>
                )}
                <p className="text-xs text-gray-500">
                    Need something else? Press <kbd className="ds-kbd">Ctrl</kbd> <kbd className="ds-kbd">K</kbd> to jump to any module, or open <Link to="/change-password" className="text-purple-700">account settings</Link>.
                </p>
            </div>
            <style>{`
                .dash-modules { display: grid; grid-template-columns: repeat(auto-fill, minmax(250px, 1fr)); gap: var(--space-2); }
                .dash-modules__cell { display: flex; cursor: grab; }
                .dash-modules__cell.is-dragging { opacity: 0.5; }
            `}</style>
        </Layout>
    );
};

export default Modules;
